import { type ReactNode } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from './utils';
import './select-field.css';

/**
 * SelectField — minimal anchored select.
 *
 * Visual contract:
 *   - Trigger: same 48px floating-label cell as `FloatingLabelInput`
 *     (h-12, rounded-md, bg-foreground/5). Two-line stack — small
 *     label up, selected value below — with a trailing chevron.
 *     When no value is selected the value slot shows the optional
 *     `placeholder`.
 *   - Content: dropdown menu anchored under the trigger (no overlay,
 *     no scrim — Radix DropdownMenu primitive). Each option is a
 *     menu item; the active one carries a leading check.
 *
 * Why DropdownMenu (and not Radix Select):
 *   - Radix Select would impose its own keyboard semantics + scrolling
 *     model. DropdownMenu is the same primitive already used elsewhere
 *     in the app and gives us the "list of clickable options" UX with
 *     less ceremony.
 *   - The cost: no native form integration. Acceptable here because
 *     the consumer already manages `value` state in URL / parent form.
 *
 * Closed list of `string` options for now — when an enum-typed select
 * is needed (e.g. `value: T`), thread a generic through the props.
 */

export interface SelectFieldProps {
  /** Floating-label text (e.g. "Tipo impresa"). */
  label: string;
  /** Currently selected option value. Empty string = no selection. */
  value: string;
  /** Closed list of options. Each value MUST be unique. */
  options: ReadonlyArray<string>;
  /** Called with the new value on selection. */
  onChange: (next: string) => void;
  /** Shown in the value slot when nothing is selected. */
  placeholder?: string;
  /** Disable the trigger (e.g. while parent form is saving). */
  disabled?: boolean;
  /** Forwarded to the trigger button. */
  className?: string;
  /** Custom render for an option label. Defaults to the value itself. */
  renderOption?: (value: string) => ReactNode;
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  className,
  renderOption,
}: SelectFieldProps): JSX.Element {
  const display = value ? (renderOption ? renderOption(value) : value) : null;
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-12 w-full items-center gap-2 rounded-md bg-foreground/5 px-3 text-left transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className="truncate text-sm text-foreground">
              {display ?? (
                <span className="text-muted-foreground">
                  {placeholder ?? '—'}
                </span>
              )}
            </span>
          </div>
          <ChevronDown
            className="h-4 w-4 flex-none text-muted-foreground"
            aria-hidden
          />
        </button>
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="start"
          sideOffset={4}
          // `--radix-dropdown-menu-trigger-width` is exposed by Radix
          // on the content element — anchoring `width` to it keeps
          // the dropdown the same width as the trigger, like a native
          // select. Inline style is the only viable way to consume the
          // CSS var (no Tailwind utility maps to it).
          // eslint-disable-next-line no-restricted-syntax -- intentional: dynamic Radix CSS variable, no Tailwind equivalent
          style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
          // Surface uses Pattern B (see select-field.css) — solid
          // `--color-bg` base + translucent `--color-surface-elevated`
          // veneer — so the dropdown stays visually consistent when it
          // floats over a translucent ancestor (form field + GlassPanel).
          // Without the solid base, alphas compose and the panel reads
          // as near-black, unlike UserMenu which opens against the
          // topbar with no translucent ancestors.
          data-select-field-content
          className="z-popover max-h-72 overflow-y-auto rounded-md p-1 text-app-text shadow-glass backdrop-blur-2xl"
        >
          {options.map((opt) => {
            const active = opt === value;
            return (
              <DropdownMenuPrimitive.Item
                key={opt}
                onSelect={() => onChange(opt)}
                // px-3 py-2 (vs the default dropdown-menu px-2 py-1.5)
                // gives the breathability of a select-list — text sits
                // ~12px from the left edge of the panel, matching the
                // trigger's px-3.
                className={cn(
                  'relative flex cursor-default select-none items-center gap-2 rounded-md px-3 py-2 text-sm outline-none transition-colors focus:bg-foreground/10 focus:text-foreground',
                  active ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                <span className="flex-1 truncate">
                  {renderOption ? renderOption(opt) : opt}
                </span>
                {active ? (
                  // Check on the RIGHT so the text is flush-left and
                  // every row aligns. Rendered conditionally (no zero-
                  // width slot eating leading padding).
                  <Check
                    className="h-3.5 w-3.5 flex-none text-foreground"
                    aria-hidden
                  />
                ) : null}
              </DropdownMenuPrimitive.Item>
            );
          })}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
